import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, ShoppingBag, Lock, Search, Link2, Zap,
} from "lucide-react";
import { PILL, StatusPill } from "./shared";
import DomainUsageWidget from "./DomainUsageWidget";

export default function DomainTab({ biz, reload, plan, onUpgrade }) {
  const [tab, setTab] = useState("buy"); // buy | own
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [buying, setBuying] = useState(null);
  const [ownDomain, setOwnDomain] = useState(biz.custom_domain || "");
  const [connecting, setConnecting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const canBuy = plan?.is_paid && plan?.domains_remaining > 0;
  const canBuyExtra = plan?.is_paid && plan?.domains_remaining <= 0;
  const extraMonthly = plan?.extra_domain_monthly_usd ?? 20;

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!search.trim()) return;
    setSearching(true); setResults([]);
    try {
      const { data } = await api.get(`/domain/search?q=${encodeURIComponent(search.trim())}`);
      setResults(data.results || []);
    } catch { toast.error("Search failed"); }
    setSearching(false);
  };
  const buy = async (domain) => {
    setBuying(domain);
    try {
      await api.post("/domain/purchase", { domain });
      toast.success(`${domain} purchased & connected!`);
      reload();
    } catch (e) {
      if (e.response?.status === 402) toast.error(e.response.data?.detail || "Upgrade to buy domains");
      else toast.error(e.response?.data?.detail || "Purchase failed");
    }
    setBuying(null);
  };
  const buyExtra = async (domain) => {
    setBuying(domain);
    try {
      const { data } = await api.post("/domain/extra-checkout", {
        domain,
        business_id: biz?.business_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setBuying(null);
    }
  };

  const connectOwn = async () => {
    setConnecting(true);
    try {
      await api.post("/domain/connect", { domain: ownDomain });
      toast.success("DNS records generated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setConnecting(false);
  };
  const verify = async (force = false) => {
    setVerifying(true);
    try {
      await api.post(force ? "/domain/force-verify" : "/domain/verify");
      toast.success(force ? "Domain connected" : "Status refreshed");
      reload();
    } catch { toast.error("Failed"); }
    setVerifying(false);
  };
  const disconnect = async () => {
    try { await api.delete("/domain"); reload(); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="nb-card p-5 mb-6">
        <div className="font-display text-2xl font-extrabold mb-1">Domain settings</div>
        <div className="text-sm text-gray-600">
          {plan?.free_subdomain
            ? "Use the free subdomain that comes with your plan, or connect a custom domain by upgrading."
            : "Your plan uses custom domains only. Pick a brand-new one or connect one you already own."}
        </div>
      </div>

      <DomainUsageWidget />

      {plan?.free_subdomain && (
        <div className="nb-card p-5 mb-4" data-testid="subdomain-card">
          <div className="text-xs uppercase font-bold text-gray-600">Free subdomain</div>
          <div className="font-mono text-lg mt-1">{biz.slug}.interesttoincome.app</div>
          <div className="text-xs text-gray-500 mt-2">Your live URL is at /site/{biz.slug}.</div>
        </div>
      )}
      {!plan?.free_subdomain && (
        <div className="nb-card-flat p-4 mb-4 bg-[#FFF7E6] text-sm" data-testid="no-subdomain-banner">
          <b>Heads up:</b> the Free subdomain is only on the Free plan. Your {plan?.plan_name} plan
          publishes on a custom domain instead.
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("buy")} data-testid="tab-buy-domain" className={`${PILL} ${tab === "buy" ? "bg-[#111] text-white" : "bg-white"}`}>
          <ShoppingBag className="w-4 h-4" /> Buy a new domain
        </button>
        <button onClick={() => setTab("own")} data-testid="tab-own-domain" className={`${PILL} ${tab === "own" ? "bg-[#111] text-white" : "bg-white"}`}>
          <Link2 className="w-4 h-4" /> I already own one
        </button>
      </div>

      {tab === "buy" && (
        <div className="nb-card p-5" data-testid="buy-domain-card">
          {!plan?.is_paid && (
            <div className="mb-4 p-4 rounded-xl bg-[#FFE6EC] border-2 border-black flex items-center gap-3" data-testid="upgrade-banner">
              <Lock className="w-5 h-5 shrink-0" />
              <div className="flex-1 text-sm">
                <div className="font-bold">Custom domains require a paid plan.</div>
                <div className="text-gray-700 mt-1">Free plans cannot purchase domains. Pick a Launch, Growth, or Scale plan to get started.</div>
              </div>
              <button onClick={onUpgrade} className={`${PILL} bg-[#8B5CF6] text-white`} data-testid="upgrade-cta-btn">
                <Zap className="w-4 h-4" /> Upgrade
              </button>
            </div>
          )}
          {plan?.is_paid && (
            <div className="mb-4 p-3 rounded-xl bg-[#F0FFF4] border-2 border-black flex items-center justify-between gap-3 flex-wrap" data-testid="allotment-banner">
              <div className="text-sm">
                <b>{plan.plan_name} plan:</b> {plan.domains_used} / {plan.included_domains} included domain{plan.included_domains === 1 ? "" : "s"} used.
                {plan.domains_remaining > 0 ? (
                  <span className="text-[#10B981] font-bold ml-1">{plan.domains_remaining} included slot{plan.domains_remaining === 1 ? "" : "s"} remaining.</span>
                ) : (
                  <span className="text-gray-700 ml-1">All included slots used — extras are <b>${extraMonthly}/mo</b> each (per domain).</span>
                )}
              </div>
              {plan.domains_remaining <= 0 && (
                <button onClick={onUpgrade} className={`${PILL} bg-[#8B5CF6] text-white text-xs`} data-testid="upgrade-for-more">
                  Upgrade plan
                </button>
              )}
            </div>
          )}
          <form onSubmit={runSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                data-testid="domain-search-input"
                placeholder="e.g. mycleaning, brightco, joe-baker"
                className="w-full pl-10 pr-3 py-3 border-2 border-black rounded-xl font-mono text-sm" />
            </div>
            <button disabled={searching} className={`${PILL} bg-[#8B5CF6] text-white`} data-testid="domain-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
            </button>
          </form>
          <div className="text-xs text-gray-500 mt-2">We check .com .co .io .ai .app .shop .store .biz</div>

          <div className="mt-5 space-y-2">
            {results.map((r) => {
              const fee = r.fees?.registration_fee ?? null;
              return (
                <div key={r.domain} className="flex flex-wrap items-center gap-3 p-3 border-2 border-black rounded-xl bg-white" data-testid={`result-${r.domain}`}>
                  <div className="font-mono font-bold flex-1 min-w-[180px]">{r.domain}</div>
                  <div className={`text-xs px-2 py-1 rounded-full border-2 border-black font-bold ${r.available ? "bg-[#10B981] text-white" : "bg-gray-200"}`}>
                    {r.available ? "Available" : "Taken"}
                  </div>
                  {r.available && (
                    <>
                      <div className="text-sm font-mono">${fee}/yr</div>
                      {!plan?.is_paid ? (
                        <button onClick={onUpgrade} className={`${PILL} bg-gray-200`} data-testid={`buy-${r.domain}`}>
                          <Lock className="w-3.5 h-3.5" /> Upgrade
                        </button>
                      ) : canBuy ? (
                        <button onClick={() => buy(r.domain)} disabled={buying === r.domain} data-testid={`buy-${r.domain}`}
                          className={`${PILL} bg-[#10B981] text-white`}>
                          {buying === r.domain ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buy (free slot)"}
                        </button>
                      ) : canBuyExtra && fee ? (
                        <button onClick={() => buyExtra(r.domain)} disabled={buying === r.domain} data-testid={`buy-extra-${r.domain}`}
                          className={`${PILL} bg-[#FF7F50] text-white`}>
                          {buying === r.domain ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buy ($${extraMonthly}/mo)`}
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              );
            })}
            {!searching && results.length === 0 && search && (
              <div className="text-sm text-gray-500 italic">No results yet — hit Search.</div>
            )}
          </div>
        </div>
      )}

      {tab === "own" && (
        <div className="nb-card p-5" data-testid="custom-domain-card">
          <div className="font-display text-lg font-extrabold mb-3">Connect a domain you already own</div>
          <div className="flex gap-2">
            <input value={ownDomain} onChange={(e) => setOwnDomain(e.target.value)} placeholder="yourbrand.com"
              data-testid="domain-input"
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg font-mono text-sm" />
            <button onClick={connectOwn} disabled={connecting || !ownDomain} className={`${PILL} bg-[#8B5CF6] text-white`} data-testid="connect-domain-btn">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect"}
            </button>
          </div>

          {biz.custom_domain && (
            <div className="mt-5">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="font-mono text-sm">{biz.custom_domain}</div>
                <StatusPill status={biz.domain_status} />
                <div className="ml-auto flex gap-2">
                  <button onClick={() => verify(false)} disabled={verifying} className={`${PILL} bg-white`} data-testid="verify-btn">
                    {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Check status
                  </button>
                  {biz.domain_status !== "connected" && (
                    <button onClick={() => verify(true)} disabled={verifying} className={`${PILL} bg-[#10B981] text-white`} data-testid="force-verify-btn">
                      Simulate connect
                    </button>
                  )}
                  <button onClick={disconnect} className={`${PILL} bg-white`} data-testid="remove-domain-btn">Remove</button>
                </div>
              </div>
              <div className="text-xs uppercase font-bold text-gray-600 mb-2">Add these DNS records at your domain provider</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-2 border-black rounded-xl overflow-hidden">
                  <thead className="bg-[#F5F0FF] border-b-2 border-black">
                    <tr>
                      <th className="text-left p-2 font-bold">Type</th><th className="text-left p-2 font-bold">Name</th>
                      <th className="text-left p-2 font-bold">Value</th><th className="text-left p-2 font-bold">TTL</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {(biz.dns_records || []).map((r, i) => (
                      <tr key={i} className="border-t border-black/20" data-testid={`dns-row-${i}`}>
                        <td className="p-2">{r.type}</td><td className="p-2">{r.name}</td>
                        <td className="p-2 break-all">{r.value}</td><td className="p-2">{r.ttl}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
